import type { FC } from 'react';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import LockRounded from '@mui/icons-material/LockRounded';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { Box, IconButton, Stack } from '@mui/material';
import shortId from 'shortid';

import {
  DATALINE_RESPONSE_DRIVER_MINIMUM,
  DatalineStatus,
  LogicalOperator,
} from 'src/constants/dataline';
import { DRAG_ITEM_TYPES } from 'src/constants/dragItem';
import type { DefinitionLineFrontend } from 'src/types/dataline';
import LogicalOperatorWrapper from 'src/components/common/Dataline/components/LogicalOperator';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

import BreakDropTarget from './components/BreakDropTarget';
import Definitionline from './components/Definitionline';
import Returns from './components/Returns';

import { styles } from './index.styles';

export const DATALINE_HEADER_TEST_ID = 'dataline-header';
export const DATALINE_BODY_TEST_ID = 'dataline-body';
export const DATALINE_READ_ONLY_TEST_ID = 'dataline-read-only';

export interface DatalineProps {
  datalineIndex: number;
  datalineRenderId: string;
  definitionlines: DefinitionLineFrontend[];
  driverAnalysis?: boolean;
  id: number | null;
  isExternal?: boolean;
  invalidities: boolean | string[];
  isReadOnly?: boolean;
  isSaved: boolean;
  name?: string;
  returns: number | null;
  status: DatalineStatus;
}

const Dataline: FC<DatalineProps> = ({
  datalineRenderId,
  definitionlines,
  id,
  datalineIndex,
  driverAnalysis = false,
  name = '',
  isExternal = false,
  invalidities = false,
  isSaved,
  isReadOnly = false,
  returns,
  status = DatalineStatus.STATUS_LOADING,
}) => {
  const { t } = useTranslation('workspace');
  const {
    deleteByRenderId,
    saveDatalineAsTemplate,
    setDriverAnalysis,
    updateDatalineName,
    updateDatalineOrder,
  } = useWorkspaceContext();
  const [folded, setFolded] = useState(isExternal);
  const [nameInput, setNameInput] = useState(name || '');
  const datalineElement = useRef<HTMLElement>(null);
  const driverAnalysisDisabled =
    returns &&
    returns < DATALINE_RESPONSE_DRIVER_MINIMUM &&
    status !== DatalineStatus.STATUS_LOADING;

  const [_, drop] = useDrop({
    accept: [
      DRAG_ITEM_TYPES.DATALINE,
      DRAG_ITEM_TYPES.DATALINE_ELEMENT,
      DRAG_ITEM_TYPES.ORG_UNIT,
      DRAG_ITEM_TYPES.SHAPING,
    ],

    hover(item: any, monitor) {
      // Do nothing if there is no dataline element and if the dragged item is not a dataline element itself.
      if (
        !datalineElement.current ||
        item.dragSourceType !== DRAG_ITEM_TYPES.DATALINE_ELEMENT
      )
        return;

      const dragIndex = item.datalineIndex;
      const hoverIndex = datalineIndex;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) return;

      const datalineElementRectangle =
        datalineElement.current.getBoundingClientRect();
      const datalineElementMiddleY =
        (datalineElementRectangle.bottom - datalineElementRectangle.top) / 2;
      const mousePosition = monitor.getClientOffset();
      if (mousePosition) {
        const mousePositionYOnDatalineElement =
          mousePosition.y - datalineElementRectangle.top;

        if (
          dragIndex < hoverIndex &&
          mousePositionYOnDatalineElement < datalineElementMiddleY
        )
          return; // Dragging downwards
        if (
          dragIndex > hoverIndex &&
          mousePositionYOnDatalineElement > datalineElementMiddleY
        )
          return; // Dragging upwards
      }

      updateDatalineOrder(dragIndex, hoverIndex);

      item.datalineIndex = hoverIndex;
    },

    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const [{ isDragging }, drag] = useDrag({
    type: DRAG_ITEM_TYPES.DATALINE_ELEMENT,
    item: {
      id,
      datalineIndex,
      dragSourceType: DRAG_ITEM_TYPES.DATALINE_ELEMENT,
      type: DRAG_ITEM_TYPES.DATALINE_ELEMENT,
    },

    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(datalineElement));

  // hook to listen for name change in state
  useEffect(() => {
    setNameInput(name);
  }, [name]);

  const changeDriverAnalysis = useCallback(
    (value: boolean) => {
      setDriverAnalysis(datalineRenderId, value);
    },
    [datalineRenderId, setDriverAnalysis],
  );

  useEffect(() => {
    if (driverAnalysisDisabled && driverAnalysis) {
      changeDriverAnalysis(false);
    }
  }, [driverAnalysisDisabled, driverAnalysis, changeDriverAnalysis]);

  const inputFieldId = useRef(shortId.generate());

  return (
    <Box sx={styles.dataline(!!invalidities, isDragging)} ref={datalineElement}>
      <Stack
        direction="row"
        spacing={1}
        sx={styles.header}
        data-test-id={DATALINE_HEADER_TEST_ID}
      >
        <Stack direction="column" sx={{ justifyContent: 'center', px: 1 }}>
          <BreakDropTarget
            datalineRenderId={datalineRenderId}
            isReadOnly={isReadOnly}
          />
        </Stack>
        <Box sx={styles.formWrapper}>
          <form onSubmit={(event) => event.preventDefault()}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                height: '100%',
              }}
            >
              <label htmlFor={inputFieldId.current}>{t('nameInput')}:</label>
              <Box sx={styles.inputWrapper}>
                <input
                  id={inputFieldId.current}
                  onFocus={(event: any) => {
                    if (!isReadOnly) event.target.select();
                  }}
                  onBlur={() => updateDatalineName(nameInput, datalineRenderId)}
                  onChange={(event: any) => setNameInput(event.target.value)}
                  readOnly={isReadOnly}
                  title={nameInput}
                  type="text"
                  value={nameInput}
                  onKeyDown={(event: any) =>
                    event.key === 'Enter' && event.target?.blur()
                  }
                />
                <IconButton
                  sx={styles.resetInputButton}
                  onClick={() => setNameInput('')}
                  disableRipple
                  size="small"
                >
                  <DeleteRounded fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          </form>
        </Box>
        <Stack direction="column" sx={{ justifyContent: 'center' }}>
          <Returns
            returnState={status}
            count={returns}
            invalidities={invalidities}
          />
        </Stack>
        <IconButton
          size="small"
          disabled={isExternal}
          onClick={() => setFolded(!folded)}
        >
          {folded ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
        </IconButton>
        <IconButton
          size="small"
          disabled={!!invalidities || isSaved || isReadOnly}
          onClick={() => {
            setNameInput(nameInput);
            saveDatalineAsTemplate(datalineRenderId);
          }}
        >
          <SaveOutlined />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => deleteByRenderId(datalineRenderId)}
          sx={styles.closeButton}
        >
          <CloseRounded />
        </IconButton>
      </Stack>
      {!folded && (
        <Stack direction="column" data-test-id={DATALINE_BODY_TEST_ID}>
          {definitionlines.map((definitionline, index) => (
            <Fragment key={definitionline.renderId}>
              {index > 0 && (
                <LogicalOperatorWrapper
                  comparisonRenderId="PlaceholderRenderId"
                  definitionlineRenderId={definitionline.renderId}
                  operator={LogicalOperator.OR}
                  isReadOnly={isReadOnly}
                />
              )}

              <Definitionline
                comparisons={definitionline.comparisons}
                definitionlineRenderId={definitionline.renderId}
                isReadOnly={isReadOnly}
              />
            </Fragment>
          ))}
        </Stack>
      )}
      {isReadOnly && (
        <Stack
          direction="row"
          sx={styles.datalineLocked}
          data-test-id={DATALINE_READ_ONLY_TEST_ID}
        >
          <Stack direction="column" sx={{ justifyContent: 'center' }}>
            {!folded ? <LockRounded sx={styles.largeLock} /> : <LockRounded />}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default Dataline;
